import { useEffect, useRef } from "react";
import L from "leaflet";

// Fix default marker icon paths for Leaflet via CDN
const ICON = new L.Icon({
  iconUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl:
    "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl:
    "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Semi-transparent moss-green circle for real carriers; muted terracotta for demo carriers.
// The stroke gives the circle a definite edge so overlapping regions still read cleanly,
// and the fill has enough opacity to be visible even when the viewport sits fully inside a circle.
const COVERAGE_STYLE = {
  real: {
    color: "#4A6B56",      // moss stroke
    weight: 2,
    fillColor: "#4A6B56",
    fillOpacity: 0.22,
  },
  demo: {
    color: "#D67B54",      // terracotta stroke
    weight: 2,
    dashArray: "6 4",
    fillColor: "#D67B54",
    fillOpacity: 0.18,
  },
};

export default function MapView({
  markers = [],
  coverage = [],           // [{carrier_id, carrier_name, label, lat, lng, radius_km, is_demo}]
  center = [54.5, -2.5],   // UK centroid (default view when no markers / no explicit center)
  zoom = 5,
  focus = null,            // {lat, lng, zoom} — imperative fly-to (Near-me / search hit)
  height = "h-[400px]",
  onMarkerClick,
  testId = "map-view",
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerLayerRef = useRef(null);
  const coverageLayerRef = useRef(null);
  const focusLayerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      center,
      zoom,
      scrollWheelZoom: false,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "© OpenStreetMap",
    }).addTo(map);
    mapRef.current = map;
    // Coverage sits BELOW markers so pins are always clickable.
    coverageLayerRef.current = L.layerGroup().addTo(map);
    markerLayerRef.current = L.layerGroup().addTo(map);
    // Focus overlay (Near-me / search-result pulse pin) sits ABOVE markers.
    focusLayerRef.current = L.layerGroup().addTo(map);
    // Mount-once semantics — center/zoom are only the INITIAL view. Adding
    // them as deps would re-run this effect on prop change and leak Leaflet
    // instances. This is the exact case react-hooks/exhaustive-deps is
    // designed to allow overriding.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Coverage circles — render whenever the coverage prop changes.
  useEffect(() => {
    if (!mapRef.current || !coverageLayerRef.current) return;
    coverageLayerRef.current.clearLayers();
    coverage.forEach((c) => {
      if (c.lat == null || c.lng == null || !c.radius_km) return;
      const style = c.is_demo ? COVERAGE_STYLE.demo : COVERAGE_STYLE.real;
      const circle = L.circle([c.lat, c.lng], {
        radius: c.radius_km * 1000, // Leaflet expects metres
        ...style,
      });
      circle.bindTooltip(
        `<div style="font-family: Manrope, sans-serif;">
          <div style="font-weight:600; color:#1B3624;">${c.carrier_name}${c.is_demo ? ' <span style="color:#D67B54; font-size:10px;">DEMO</span>' : ""}</div>
          <div style="font-size:11px; color:#6B6965;">${c.label || "Service area"} · ${c.radius_km} km radius</div>
        </div>`,
        { sticky: true, direction: "top", opacity: 0.95 }
      );
      circle.addTo(coverageLayerRef.current);
    });
  }, [coverage]);

  // Markers — pins for waste posts / bin listings.
  useEffect(() => {
    if (!mapRef.current || !markerLayerRef.current) return;
    markerLayerRef.current.clearLayers();
    const group = [];
    markers.forEach((m) => {
      const marker = L.marker([m.lat, m.lng], { icon: ICON });
      const demoBadge = m.is_demo
        ? '<span style="display:inline-block; background:#D67B54; color:#fff; font-size:9px; font-weight:700; padding:2px 6px; border-radius:999px; letter-spacing:1px; margin-left:6px;">DEMO</span>'
        : "";
      marker.bindPopup(
        `<div style="font-family: Manrope, sans-serif; min-width: 180px;">
          <div style="font-weight:600; margin-bottom:4px; color:#1B3624;">${m.title}${demoBadge}</div>
          <div style="font-size:12px; color:#6B6965;">${m.subtitle || ""}</div>
          ${m.price ? `<div style="margin-top:6px; color:#4A6B56; font-weight:600;">${m.price}</div>` : ""}
        </div>`
      );
      if (onMarkerClick) marker.on("click", () => onMarkerClick(m));
      marker.addTo(markerLayerRef.current);
      group.push([m.lat, m.lng]);
    });
    // Auto-fit strategy: prefer showing both markers AND at least the edges
    // of nearby coverage circles so customers instantly see "there's a licensed
    // carrier who covers this area". Falls back to UK-wide when nothing's here.
    // But: once the caller has taken imperative control via the `focus` prop
    // (Near-me tap or search hit), don't yank the map back to the auto-fit
    // bounds every time the marker list churns — that would defeat the
    // whole point of flying to the user's location.
    if (focus && focus.lat != null && focus.lng != null) {
      // Focus effect handles the view; auto-fit is suppressed.
    } else if (group.length > 1 || (group.length === 1 && coverage.length)) {
      const bounds = L.latLngBounds(group);
      // Also include coverage centers so at least one carrier area is visible
      // when the markers happen to sit near its edge.
      coverage.forEach((c) => bounds.extend([c.lat, c.lng]));
      mapRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 11 });
    } else if (group.length === 1) {
      mapRef.current.setView(group[0], 12);
    } else if (coverage.length) {
      // No markers but coverage present — fit to the union of coverage circles.
      const bounds = L.latLngBounds(coverage.map((c) => [c.lat, c.lng]));
      mapRef.current.fitBounds(bounds, { padding: [60, 60], maxZoom: 8 });
    }
  }, [markers, coverage, onMarkerClick, focus]);

  // Imperative fly-to. Fires when the parent hands us a `focus` (Near-me
  // resolved to real coords, or the search bar geocoded a place). We draw
  // a subtle "you are here" marker so the customer sees where the map
  // just moved to, and animate rather than snap so the transition reads
  // as an intentional action rather than a page re-render.
  useEffect(() => {
    if (!mapRef.current || !focusLayerRef.current) return;
    focusLayerRef.current.clearLayers();
    if (!focus || focus.lat == null || focus.lng == null) return;
    const targetZoom = focus.zoom ?? 13;
    mapRef.current.flyTo([focus.lat, focus.lng], targetZoom, {
      duration: 0.9,
    });
    // Terracotta pulse marker so users can spot where the map centered.
    const dot = L.circleMarker([focus.lat, focus.lng], {
      radius: 8,
      color: "#D67B54",
      weight: 3,
      fillColor: "#D67B54",
      fillOpacity: 0.75,
    });
    if (focus.label) {
      dot.bindTooltip(focus.label, {
        permanent: false,
        direction: "top",
        opacity: 0.95,
      });
    }
    dot.addTo(focusLayerRef.current);
  }, [focus]);

  return (
    <div
      ref={containerRef}
      data-testid={testId}
      className={`${height} w-full rounded-2xl overflow-hidden border border-[#E2DFD8]`}
    />
  );
}
